/**
 * AuditSessionDetailContent Component
 *
 * Displays the main content section with header and tab navigation for audit session detail page.
 */

import { TabNavigation } from '@core/components/TabNavigation';
import { AuditItemsTab } from '@internal-audit/components/AuditSessionTabNavigation/AuditItemsTab';
import { AuditReportsTab } from '@internal-audit/components/AuditSessionTabNavigation/AuditReportsTab';
import { CommunityAuditDetailHeaderSection, AuditDetailSection } from '@internal-audit/components/CommunityAuditDetailHeaderSection';
import type { FlexibleAuditSession, GraphQLCommunity } from '@internal-audit/domain/graphql-types';

import * as styles from '../AuditSessionDetailPage.css';

interface AuditSessionDetailContentProps {
  /** Community data */
  community: GraphQLCommunity;
  /** Session data */
  session: FlexibleAuditSession;
  /** Community ID */
  communityId: string;
  /** Session ID */
  sessionId: string;
  /** Handler for complete audit action */
  onCompleteAudit: () => void;
  /** Handler for delete audit session action */
  onDeleteAuditSession: () => void;
}

export function AuditSessionDetailContent({
  community,
  session,
  communityId,
  sessionId,
  onCompleteAudit,
  onDeleteAuditSession,
}: AuditSessionDetailContentProps) {
  return (
    <div>
      {/* Header Section */}
      <div>
        {/* Community Audit Detail Header Section */}
        <CommunityAuditDetailHeaderSection community={community} isLoading={false} session={session} />
      </div>

      {/* Content Section with Solid Background */}
      <div className={styles.contentSection}>
        <div className={styles.contentContainer}>
          {/* Audit Detail Section (session info, charts, actions) */}
          <AuditDetailSection session={session} onCompleteAudit={onCompleteAudit} onDeleteAuditSession={onDeleteAuditSession} />

          {/* Tab Navigation for Audit Items and Reports */}
          <TabNavigation
            ariaLabel="Audit session sections"
            defaultActiveTab="audit-items"
            tabs={[
              {
                key: 'audit-items',
                label: 'Audit Items',
                content: (
                  <AuditItemsTab
                    community={community}
                    communityId={communityId}
                    session={session}
                    sessionId={sessionId}
                    onCompleteAudit={onCompleteAudit}
                    onDeleteAuditSession={onDeleteAuditSession}
                  />
                ),
              },
              {
                key: 'reports',
                label: 'Reports',
                disabled: !session?.completedAt, // Disable if audit session is not completed
                disabledTooltip: 'Reports are not available until completion of an audit',
                content: <AuditReportsTab communityId={communityId} sessionId={sessionId} />,
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
}
